import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import Avatar from "../common/Avatar";
import { ROLES } from "../../config/roles";
import { shareService, agentService, clubService } from "../../services/api";
import { buildPickerSections, rowIsRequestable } from "./pickerRecipients";
import MessageRequestComposeModal from "./MessageRequestComposeModal";

// NewChatModal — the role-scoped "+" new-chat picker (design frame 5m, D27/D12).
//
// Two sections: DIRECT (contacts & colleagues → "Chat") and EVERYONE ELSE — VIA
// MESSAGE REQUEST (cross-agency + clubs → "Request", opens the compose gate).
// The picker NEVER bypasses the message-request gate: a request row routes
// through MessageRequestComposeModal (which enforces the D12 cap), never a direct
// create. Two backend sources feed it: /recipients (same-org colleagues, always
// DIRECT) and /recipient-search (cross-party, tagged reachable_via request|direct
// with a role_scope). See pickerRecipients.js for the pure bucketing.
//
// Club request rows appear per 5m but stay DISABLED: the compose flow keys on the
// Agent-row PK, and there is no agent→club request flow yet — shown honestly, not
// bypassed. DM-only roles (role_scope "contacts_only") get no request section.
//
// Props:
//   onOpenThread (channelId|cid) => void  — deep-link into the opened chat.
//   onClose      () => void
const NewChatModal = ({ onOpenThread, onClose, onViewPending }) => {
  const [query, setQuery] = useState("");
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState([]);
  const [roleScope, setRoleScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openingId, setOpeningId] = useState(null);
  const [requestRecipient, setRequestRecipient] = useState(null);

  const currentRole = useSelector((state) => state.auth.currentRole);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "unset";
    };
  }, [onClose]);

  // Debounced fetch of BOTH sources in parallel: same-org colleagues (DIRECT) +
  // cross-party search (VIA REQUEST / direct per role). Either may fail
  // independently without blanking the other.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const handle = setTimeout(async () => {
      const [recipRes, searchRes] = await Promise.all([
        shareService.getRecipients(query.trim()),
        shareService.recipientSearch(query.trim()),
      ]);
      if (cancelled) return;
      setContacts(recipRes.success && Array.isArray(recipRes.data) ? recipRes.data : []);
      setSearch(searchRes.success && Array.isArray(searchRes.data) ? searchRes.data : []);
      setRoleScope(searchRes.roleScope || null);
      setLoading(false);
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  const { direct, request } = useMemo(
    () => buildPickerSections({ contacts, search }),
    [contacts, search],
  );

  const openChatWith = async (row) => {
    if (openingId) return;
    setOpeningId(row.key);
    try {
      if (row.channel_id_candidate) {
        onOpenThread?.(row.channel_id_candidate);
        onClose?.();
        return;
      }
      if (!row.account_id) {
        toast.error("Couldn't start this chat.");
        return;
      }
      const svc = currentRole === ROLES.ClubAdmin ? clubService : agentService;
      const res = await svc.createAgentDirectChat(row.account_id);
      if (res?.success && res.data?.channel_id) {
        onOpenThread?.(res.data.channel_id);
        onClose?.();
      } else {
        toast.error(res?.error || "Couldn't start this chat.");
      }
    } catch (e) {
      toast.error("Couldn't start this chat.");
    } finally {
      setOpeningId(null);
    }
  };

  // Route a request row through the message-request gate (never a direct create).
  const startRequest = (row) => {
    if (!rowIsRequestable(row)) {
      toast.error("This contact can't be requested yet.");
      return;
    }
    setRequestRecipient({
      agent_id: row.agent_id,
      name: row.name,
      subtitle: row.subtitle,
      profile_picture_url: row.avatar,
    });
  };

  const renderRow = (row) => {
    const isRequest = row.bucket === "request";
    const canRequest = rowIsRequestable(row);
    return (
      <div key={row.key} className="flex items-center gap-3 rounded-[10px] px-3 py-2.5">
        <Avatar src={row.avatar} alt={row.name} fallbackText={row.name} size={38} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[13.5px] font-semibold text-[#101828]">
            {row.name}
          </span>
          <span className="truncate text-[11.5px] text-[#667085]">{row.subtitle}</span>
        </div>
        {isRequest ? (
          <button
            type="button"
            onClick={() => startRequest(row)}
            disabled={!canRequest}
            title={canRequest ? "Send a message request" : "Not available yet"}
            className="flex-shrink-0 rounded-[9px] border border-[#BBD7FF] px-3.5 py-1.5 text-[12.5px] font-semibold text-[#1466D8] disabled:opacity-50"
          >
            Request
          </button>
        ) : (
          <button
            type="button"
            onClick={() => openChatWith(row)}
            disabled={Boolean(openingId)}
            className="flex-shrink-0 rounded-[9px] bg-[#2283FF] px-3.5 py-1.5 text-[12.5px] font-semibold text-white disabled:opacity-60"
          >
            {openingId === row.key ? (
              <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/50 border-t-white" />
            ) : (
              "Chat"
            )}
          </button>
        )}
      </div>
    );
  };

  const SectionLabel = ({ children }) => (
    <div
      className="px-3 pt-3 pb-1"
      style={{
        font: "600 9.5px ui-monospace,Menlo,monospace",
        color: "#98A2B3",
        letterSpacing: ".06em",
      }}
    >
      {children}
    </div>
  );

  const nothing = !loading && direct.length === 0 && request.length === 0;
  const contactsOnly = roleScope === "contacts_only";

  return createPortal(
    <>
      <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[10vh]" role="dialog" aria-modal="true">
        <div className="absolute inset-0 bg-black/45" onClick={onClose} aria-hidden="true" />
        <div
          className="relative flex w-full flex-col bg-white"
          style={{ maxWidth: 440, borderRadius: 16, boxShadow: "0 24px 64px rgba(9,14,26,.45)", maxHeight: "72vh" }}
        >
          <div className="flex items-center justify-between px-5 pt-4 pb-3">
            <span className="text-[15.5px] font-bold text-[#101828]">New chat</span>
            <button
              type="button"
              onClick={onClose}
              className="text-[#98A2B3] hover:text-[#101828]"
              aria-label="Close"
            >
              <svg width="16" height="16" viewBox="0 0 12 12" fill="none">
                <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="px-5 pb-3">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#98A2B3]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.6} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={query}
                autoFocus
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search people and clubs"
                className="w-full rounded-[10px] border border-[#E4E9F0] py-2.5 pl-9 pr-3 text-[13.5px] text-[#101828] placeholder:text-[#98A2B3] focus:border-[#2283FF] focus:outline-none"
              />
            </div>
          </div>

          <div className="min-h-[120px] flex-1 overflow-y-auto px-2 pb-1">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#DDE3EC] border-t-[#2283FF]" />
              </div>
            ) : nothing ? (
              <p className="px-3 py-8 text-center text-[12.5px] text-[#98A2B3]">
                {contactsOnly
                  ? "You can continue your existing conversations from the inbox."
                  : query.trim()
                  ? "No people match that search."
                  : "No contacts available."}
              </p>
            ) : (
              <>
                {direct.length > 0 && (
                  <>
                    <SectionLabel>DIRECT — YOUR CONTACTS &amp; COLLEAGUES</SectionLabel>
                    {direct.map(renderRow)}
                  </>
                )}
                {request.length > 0 && (
                  <>
                    <SectionLabel>EVERYONE ELSE — VIA MESSAGE REQUEST</SectionLabel>
                    {request.map(renderRow)}
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer copy verbatim (5m). */}
          <div className="border-t border-[#F0F2F6] px-5 py-3">
            <p className="text-[11px] leading-[1.5] text-[#98A2B3]">
              Recipients you may not message simply don&apos;t appear. Requests
              count toward your 20 pending (D12).
            </p>
          </div>
        </div>
      </div>

      {/* D12 gate: the request compose flow (never a direct create). */}
      {requestRecipient && (
        <MessageRequestComposeModal
          recipient={requestRecipient}
          onClose={() => setRequestRecipient(null)}
          onSent={() => {
            setRequestRecipient(null);
            onClose?.();
          }}
          onSameOrg={({ channelId }) => {
            setRequestRecipient(null);
            if (channelId) onOpenThread?.(channelId);
            onClose?.();
          }}
          /* Frame 1r escape hatch — the picker already lives inside the inbox,
             so close down to it and open the gate's Sent view. */
          onViewPending={() => {
            setRequestRecipient(null);
            onClose?.();
            onViewPending?.();
          }}
        />
      )}
    </>,
    document.body,
  );
};

export default NewChatModal;
